import React, { useState, useEffect, useRef } from 'react';
import { Shield, HardHat, Star, CheckCircle2, ChevronRight, Calendar, Quote, ChevronLeft, ChevronRight as ChevronRightIcon, Smartphone, Mail, Building, User, Info, Loader2, AlertOctagon, AlertTriangle, Users, Scale, FileText, Activity, MessageSquareMore } from 'lucide-react';

const LandingSST = () => {
  // CONFIGURACIÓN DE WHATSAPP DIRECTO PARA CONTRATACIÓN FAST-TRACK
  const WHATSAPP_NUMBER = "573114044203"; // Formato internacional completo para Colombia
  const WHATSAPP_MESSAGE = "¡Hola, Zaphy! 👷‍♂️ Vi su portafolio de Seguridad y Salud en el Trabajo y estamos listos para delegar de forma segura el SG-SST de nuestra PyME bajo Co-Responsabilidad Legal. ¿Nos ayudan con el proceso de contratación rápida?";

  // Estados de control de información corporativa y flujo
  const [leadData, setLeadData] = useState({ name: '', company: '', phone: '', email: '' });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isLeadCaptured, setIsLeadCaptured] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [totalScore, setTotalScore] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [notification, setNotification] = useState(null);

  // Referencias de navegación interna
  const testSectionRef = useRef(null);
  const reviewsContainerRef = useRef(null);

  // EFECTO RADICAL CONTROLADO: Elimina la barra superior morada institucional del Navbar y elementos del tema base
  useEffect(() => {
    const hideGlobalThemeElements = () => {
      const classesToHide = [
        '.top-bar', '#top-nav-bar', '.header-wrapper', '.purple-bar',
        '.z-\\[60\\]', '.h-9.bg-gradient-to-r', 'nav:not(.landing-header-nav)',
        'header:not(.landing-header)', 'footer:not(.landing-footer)', 
        '.footer-base', '#masthead', '#colophon', '.wp-block-template-part'
      ];
      
      classesToHide.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          if (el) el.style.setProperty('display', 'none', 'important');
        });
      });
    };

    hideGlobalThemeElements();
    const intervalId = setInterval(hideGlobalThemeElements, 100);

    return () => {
      clearInterval(intervalId);
      const classesToHide = [
        '.top-bar', '#top-nav-bar', '.header-wrapper', '.purple-bar',
        'header:not(.landing-header)', 'nav', 'footer:not(.landing-footer)', 
        '.footer-base', '#masthead', '#colophon'
      ];
      classesToHide.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => { if (el) el.style.display = ''; });
      });
    };
  }, []);

  const GOOGLE_REVIEWS = [
    { id: 1, author_name: "Ivan Muñoz", relative_time_description: "hace 5 días", rating: 5, text: "Empresa sólida, seria, responsable y con amplios conocimientos en su área. Se caracteriza por su honestidad y ayuda a resolver cualquier reto.", initial: "I" },
    { id: 2, author_name: "Eliana Astrid Susatama Blanco", relative_time_description: "hace 2 años", rating: 4, text: "Es una empresa que brinda buena asesoría y apoyo a nivel corporativo muy buen complemento para la empresa en Colombia 👏", initial: "E" },
    { id: 3, author_name: "Luis José Pulido Arango", relative_time_description: "hace 2 años", rating: 4, text: "Sigmac es una empresa comprometida con sus clientes. Ese compromiso permite que evolucione y día a día vaya mejorando en sus servicios", initial: "L" },
    { id: 4, author_name: "rene corrales", relative_time_description: "hace 2 años", rating: 5, text: "Empresa muy seria y que atiende con rapidez y eficiencia las necesidades referentes a la parte laboral de mi empresa. La recomiendo ampliamente", initial: "R" },
    { id: 5, author_name: "Daniel Villate", relative_time_description: "hace 2 años", rating: 4, text: "Es una empresa organizada y tienen buena experiencia en los temas a trabajar.", initial: "D" }
  ];

  const questions = [
    {
      id: 1,
      question: "¿Tu empresa cuenta actualmente con el SG-SST diseñado, implementado y al día?",
      options: [
        { text: "Sí, implementado al 100% y actualizado.", points: 100 },
        { text: "Tenemos el documento, pero nos cuesta ejecutarlo en el día a día.", points: 50 },
        { text: "No tenemos nada implementado o está desactualizado hace años.", points: 0 }
      ]
    },
    {
      id: 2,
      question: "¿Cómo calificarías la participación activa de tus empleados en las capacitaciones obligatorias de ley?",
      options: [
        { text: "Excelente. Participan de forma activa y les gusta la dinámica.", points: 100 },
        { text: "Asisten por obligación de la gerencia, pero ignoran el contenido.", points: 40 },
        { text: "No realizamos capacitaciones de SST periódicamente.", points: 0 }
      ]
    },
    {
      id: 3,
      question: "En caso de ocurrir un accidente laboral grave en la empresa, ¿tienes un protocolo de defense legal listo?",
      options: [
        { text: "Sí, tenemos acompañamiento de abogados laboralistas y ARL listos.", points: 100 },
        { text: "No estoy seguro de los alcances. Nos tomaría tiempo reaccionar.", points: 30 },
        { text: "No, no sabríamos cómo blindarnos legalmente ante una demanda.", points: 0 }
      ]
    },
    {
      id: 4,
      question: "Si el Ministerio de Trabajo te hace una inspección sorpresa, ¿puedes entregar los soportes del año en menos de 1 hora?",
      options: [
        { text: "Sí, todo está digitalizado, firmado y en orden.", points: 100 },
        { text: "Me tomaría al menos 3 días recopilar las firmas físicas de las carpetas.", points: 50 },
        { text: "Imposible, tenemos vacíos documentales enormes en el histórico.", points: 0 }
      ]
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLeadData(prev => ({ ...prev, [name]: value }));
  };

  const handleLeadSubmit = (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      alert("Por favor acepta la política de tratamiento de datos corporativos para continuar.");
      return;
    }
    setIsLeadCaptured(true);
  };

  const handleOptionSelect = async (points) => {
    const nextScore = totalScore + points;
    setTotalScore(nextScore);
    
    if (currentStep < questions.length) {
      setCurrentStep(prev => prev + 1);
    } else {
      const finalPercentage = Math.round((nextScore / 400) * 100);
      setShowResults(true);
      await sendLeadToWordPress(finalPercentage);
    }
  };

  const sendLeadToWordPress = async (score) => {
    setIsSending(true);
    try {
      const siteUrl = window.zaphyData?.siteUrl || 'https://zaphy.com.co';
      const formId = '8003'; 

      const data = new FormData();
      data.append('your-name', leadData.name);
      data.append('your-email', leadData.email);
      data.append('your-phone', leadData.phone);
      data.append('company', leadData.company); 
      data.append('your-subject', `Lead Ads Diagnóstico SST (${score}%) - ${leadData.company}`);
      
      const messageBody = `
NUEVA RESPUESTA PROCESADA DESDE LA LANDING DE ADS SST

• Empresa: ${leadData.company}
• Representante: ${leadData.name}
• WhatsApp Directo: ${leadData.phone}
• Email de Contacto: ${leadData.email}

--- METRICAS DEL TEST FLASH ---
Porcentaje final de blindaje normativo estimado: ${score}% / 100%
      `.trim();
      
      data.append('your-message', messageBody);

      data.append('_wpcf7', formId);
      data.append('_wpcf7_version', '5.9');
      data.append('_wpcf7_locale', 'es_ES');
      data.append('_wpcf7_unit_tag', `wpcf7-f${formId}-p1-o1`); 
      data.append('_wpcf7_container_post', '0');

      const response = await fetch(`${siteUrl}/wp-json/contact-form-7/v1/contact-forms/${formId}/feedback`, {
        method: 'POST',
        body: data
      });

      const result = await response.json();
      console.log('Control Técnico de Recepción API SST (Form 8003):', result);

      if (result.status === 'mail_sent') {
        setNotification("Generamos tu resultado, agenda tu diagnóstico.");
      } else {
        console.warn("API de WordPress procesó la información con advertencia local:", result.status);
        setNotification("Resultado generado. Redirigiendo a la agenda...");
      }

      setTimeout(() => setNotification(null), 5000);
    } catch (error) {
      console.error("Error crítico al conectar con la API de WordPress en formulario 8003:", error);
    } finally {
      setIsSending(false);
    }
  };

  const getDynamicShieldStatus = () => {
    const finalScore = Math.round((totalScore / 400) * 100);
    
    if (finalScore <= 50) {
      return {
        score: finalScore,
        textColor: "text-red-600",
        badge: "ALARMA CRÍTICA / EXPOSICIÓN TOTAL",
        badgeStyle: "bg-red-100 text-red-700",
        icon: <AlertOctagon size={64} className="text-red-600 animate-bounce" />,
        desc: "Tu empresa está en una situación de vulnerabilidad extrema. De ocurrir un accidente laboral, la ausencia de soportes físicos y digitales válidos expone el patrimonio del negocio a responsabilidades civiles y penales directas para la gerencia."
      };
    } else if (finalScore <= 70) {
      return {
        score: finalScore,
        textColor: "text-orange-500",
        badge: "ALERTA / CUMPLIMIENTO PARCIAL",
        badgeStyle: "bg-orange-100 text-orange-700",
        icon: <AlertTriangle size={64} className="text-orange-500 animate-pulse" />,
        desc: "Cuentas con documentos base, pero careces de evidencias de campo dinámicas. Ante una inspección sorpresa del Ministerio de Trabajo en Medellín, te encuentras expuesto a sanciones por vacíos documentales menores."
      };
    } else if (finalScore <= 90) {
      return {
        score: finalScore,
        textColor: "text-emerald-600",
        badge: "BUEN BLINDAJE / EXPOSICIÓN BAJA",
        badgeStyle: "bg-emerald-100 text-emerald-700",
        icon: <Shield size={64} className="text-emerald-600 shrink-0" />,
        desc: "¡Excelente gestión preventiva! Tu esqueleto legal está protegido. Sin embargo, mantener los registros actualizados consume valiosas horas administrativas. En Zaphy asumimos esa carga para que te enfoques al 100% en crecer."
      };
    } else {
      return {
        score: finalScore,
        textColor: "text-blue-600",
        badge: "BLINDAJE PREMIUM / PROTECCIÓN ABSOLUTA",
        badgeStyle: "bg-blue-100 text-blue-700",
        icon: <Shield size={64} className="text-blue-600 shrink-0" />,
        desc: "Tu nivel de protección cumple de forma milimétrica con la legislación laboral colombiana vigente. Zaphy puede automatizar todo este control diario para que mantengas este estándar de excelencia sin esfuerzo."
      };
    }
  };

  const status = getDynamicShieldStatus();

  const handleScrollReviews = (direction) => {
    if (reviewsContainerRef.current) {
      const container = reviewsContainerRef.current;
      const scrollAmount = container.clientWidth * 0.8;
      container.scrollTo({
        left: direction === 'left' ? container.scrollLeft - scrollAmount : container.scrollLeft + scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollToTest = () => {
    if (testSectionRef.current) {
      testSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const calendarLink = "https://calendar.app.google/UYZ2ERubo552yRg19";

  const handleCalendarRedirect = () => {
    const finalCalendarLink = `${calendarLink}?name=${encodeURIComponent(leadData.name)}&email=${encodeURIComponent(leadData.email)}&answers=ScoreSST_${status.score}%_Empresa_${encodeURIComponent(leadData.company)}`;
    window.open(finalCalendarLink, '_blank');
  };

  const handleWhatsAppFastTrack = () => {
    const targetUrl = `https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;
    window.open(targetUrl, '_blank');
  };

  return (
    <div className="bg-[#F8FAFC] text-slate-800 font-[Poppins] antialiased min-h-screen relative overflow-x-hidden">
      
      {notification && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] w-[calc(100%-2rem)] max-w-md p-4">
          <div className="bg-slate-900 text-white p-4 rounded-xl shadow-2xl flex items-center gap-3 border border-emerald-500/30">
            <Info className="text-emerald-400 shrink-0" size={20} />
            <p className="text-xs font-semibold">{notification}</p>
          </div>
        </div>
      )}

      {/* Header Exclusivo de Conversión */}
      <header className="landing-header bg-white border-b border-slate-100 py-6 relative z-30">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="https://zaphy.com.co" className="hover:opacity-95 transition-all">
              <img src="https://zaphy.com.co/wp-content/uploads/2021/07/cropped-LOGO-ZAPHY.png" alt="Zaphy" className="h-16 w-auto md:h-20" />
            </a>
            <div className="h-10 w-px bg-slate-200 mx-2 hidden sm:block"></div>
            <span className="text-sm md:text-base tracking-wider text-emerald-600 font-black uppercase hidden sm:block">
              SST Especializado PyMEs
            </span>
          </div>

          <button 
            onClick={scrollToTest}
            className="bg-[#0F172A] hover:bg-slate-800 text-white font-extrabold text-xs md:text-sm py-3 px-5 rounded-full shadow-lg transition-all flex items-center gap-2 tracking-wide uppercase border border-slate-700"
          >
            Comprobar Cumplimiento
            <Shield size={14} className="text-orange-500" fill="currentColor" />
          </button>
        </div>
      </header>

      {/* Hero Body */}
      <main className="max-w-7xl mx-auto px-4 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        
        <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-900 rounded-full py-1.5 px-4 text-xs md:text-sm font-semibold">
            🛡️ Consulta de Seguridad y Salud en el Trabajo en Medellín
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-[#0F172A] tracking-tight leading-[1.12]">
            Somos tu <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-emerald-500">Área de SST</span>. <br />
            No estás solo, respondemos por ti.
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed font-medium">
            Delegar la gestión de riesgos laborales no tiene por qué restarte tranquilidad. En Zaphy asumimos la operación integral bajo un acuerdo formal de <strong className="text-slate-900 font-bold">Co-Responsabilidad Legal</strong>. Protegemos a tus colaboradores, respondemos ante auditorías y evitamos multas administrativas para que te enfoques en expandir tu PyME.
          </p>
        </div>

        {/* Card Contenedora del Test */}
        <div ref={testSectionRef} className="lg:col-span-5 w-full">
          <div className="bg-white rounded-[32px] shadow-xl border border-slate-100 p-6 md:p-8">
            
            {/* Formulario Corporativo */}
            {!isLeadCaptured && (
              <div className="space-y-5">
                <div className="border-b border-slate-100 pb-3 text-center">
                  <span className="text-xs font-bold text-orange-600 uppercase tracking-widest block">DIAGNÓSTICO INICIAL SST</span>
                  <h3 className="font-extrabold text-[#0F172A] text-lg mt-0.5">Ingresa tus datos corporativos</h3>
                </div>
                <form onSubmit={handleLeadSubmit} className="space-y-4">
                  <div className="relative">
                    <User className="absolute left-4 top-3.5 text-slate-400" size={18} />
                    <input type="text" name="name" required value={leadData.name} onChange={handleInputChange} className="w-full border border-slate-200 bg-slate-50/50 rounded-xl p-3.5 pl-11 text-sm outline-none focus:border-blue-600 focus:bg-white transition-all font-medium" placeholder="Tu Nombre y Apellido" />
                  </div>
                  <div className="relative">
                    <Building className="absolute left-4 top-3.5 text-slate-400" size={18} />
                    <input type="text" name="company" required value={leadData.company} onChange={handleInputChange} className="w-full border border-slate-200 bg-slate-50/50 rounded-xl p-3.5 pl-11 text-sm outline-none focus:border-blue-600 focus:bg-white transition-all font-medium" placeholder="Razón Social / Empresa" />
                  </div>
                  <div className="relative">
                    <Smartphone className="absolute left-4 top-3.5 text-slate-400" size={18} />
                    <input type="tel" name="phone" required value={leadData.phone} onChange={handleInputChange} className="w-full border border-slate-200 bg-slate-50/50 rounded-xl p-3.5 pl-11 text-sm outline-none focus:border-blue-600 focus:bg-white transition-all font-medium" placeholder="Celular / WhatsApp" />
                  </div>
                  <div className="relative">
                    <Mail className="absolute left-4 top-3.5 text-slate-400" size={18} />
                    <input type="email" name="email" required value={leadData.email} onChange={handleInputChange} className="w-full border border-slate-200 bg-slate-50/50 rounded-xl p-3.5 pl-11 text-sm outline-none focus:border-blue-600 focus:bg-white transition-all font-medium" placeholder="Correo Corporativo" />
                  </div>
                  
                  {/* Checkbox Obligatorio Habeas Data */}
                  <div className="flex items-start gap-3 pt-1 px-1">
                    <input 
                      type="checkbox" 
                      id="data-policy" 
                      required 
                      checked={acceptedTerms} 
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded text-blue-600 focus:ring-blue-500 border-slate-300 cursor-pointer" 
                    />
                    <label htmlFor="data-policy" className="text-[11px] text-slate-500 leading-snug font-medium cursor-pointer">
                      Acepto el tratamiento de mis datos de acuerdo con la{' '}
                      <a href="https://zaphy.com.co/politica" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline font-bold hover:text-blue-800">
                        Política de Privacidad
                      </a>{' '}
                      de Zaphy.
                    </label>
                  </div>

                  <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-extrabold py-4 rounded-xl text-sm transition-all shadow-md tracking-wider uppercase mt-2">Iniciar Cuestionario</button>
                  
                  {/* Separador estético */}
                  <div className="relative flex py-2 items-center">
                    <div className="flex-grow border-t border-slate-200"></div>
                    <span className="flex-shrink mx-4 text-slate-400 text-[11px] font-bold tracking-widest uppercase">O también</span>
                    <div className="flex-grow border-t border-slate-200"></div>
                  </div>

                  {/* Botón WhatsApp Fast-Track Integrado */}
                  <button 
                    type="button"
                    onClick={handleWhatsAppFastTrack}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold py-3.5 rounded-xl text-sm transition-all shadow-sm flex items-center justify-center gap-2 tracking-wide uppercase"
                  >
                    <MessageSquareMore size={18} />
                    Contratar ya por WhatsApp
                  </button>
                </form>
              </div>
            )}

            {/* Cuestionario Multi-pasos */}
            {isLeadCaptured && !showResults && (
              <div className="space-y-4 animate-[fadeIn_0.2s]">
                <div className="flex justify-between items-center text-xs font-semibold text-slate-400">
                  <span className="text-orange-600 font-bold uppercase">Cuestionario</span>
                  <span>Pregunta {currentStep} de 4</span>
                </div>
                <div className="w-full bg-slate-100 h-2 rounded-full">
                  <div className="bg-blue-700 h-2 rounded-full transition-all duration-300" style={{ width: `${(currentStep / 4) * 100}%` }}></div>
                </div>

                <div className="pt-2 space-y-4">
                  <h4 className="text-sm md:text-base font-bold text-[#0F172A] leading-snug">{questions[currentStep - 1].question}</h4>
                  <div className="grid gap-2.5">
                    {questions[currentStep - 1].options.map((opt, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleOptionSelect(opt.points)}
                        className="w-full text-left p-4 rounded-xl border border-slate-200 hover:border-blue-700 hover:bg-slate-50/50 transition-all flex justify-between items-center group"
                      >
                        <span className="text-xs md:text-sm font-medium text-slate-700">{opt.text}</span>
                        <ChevronRight size={16} className="text-slate-400 group-hover:text-blue-700 shrink-0 ml-2" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Pantalla Final de Resultados */}
            {showResults && (
              <div className="text-center space-y-5 animate-[fadeIn_0.3s]">
                <div className="w-20 h-20 bg-slate-50 mx-auto rounded-2xl flex items-center justify-center border border-slate-100 shadow-sm relative">
                  {status.score <= 70 && <div className={`absolute inset-0 rounded-2xl animate-ping opacity-15 ${status.score <= 50 ? 'bg-red-500' : 'bg-orange-500'}`}></div>}
                  {status.icon}
                </div>
                
                <div>
                  <span className="text-[10px] font-bold text-slate-400 block uppercase tracking-widest">Resultado Procesado</span>
                  <h4 className="text-2xl font-black text-slate-900 mt-1">Tu Blindaje: <span className={status.textColor}>{status.score}/100</span></h4>
                  <span className={`inline-block text-[9px] font-extrabold px-3 py-1 rounded-full mt-1.5 ${status.badgeStyle}`}>{status.badge}</span>
                </div>

                <p className="text-slate-600 text-xs md:text-sm leading-relaxed px-1">{status.desc}</p>

                <div className="bg-slate-50 p-4 rounded-xl border text-left">
                  <span className="text-[10px] font-bold text-slate-400 block uppercase mb-1.5">En tu consultoría de 30 minutos revisaremos:</span>
                  <p className="text-xs text-slate-700 font-medium">✓ Mitigación de riesgos basada en tu score de {status.score}%</p>
                  <p className="text-xs text-slate-700 font-medium mt-1">✓ Plan estratégico anticaídas de auditorías</p>
                </div>

                <button 
                  onClick={handleCalendarRedirect}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white font-extrabold py-4 px-6 rounded-xl transition-all shadow-lg shadow-orange-500/20 flex items-center justify-center gap-2 text-sm uppercase tracking-wider"
                >
                  {isSending ? (
                    <span className="flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Guardando Reporte...</span>
                  ) : (
                    <span className="flex items-center gap-2">Agendar Diagnóstico Gratis <Calendar size={16} /></span>
                  )}
                </button>
              </div>
            )}

          </div>
        </div>
      </main>

      {/* Pilares de Valor de Alta Densidad */}
      <section className="bg-[#0F172A] text-white py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 text-center space-y-3 mb-16">
          <span className="text-xs font-bold text-emerald-400 tracking-widest uppercase">Cumplimiento Operativo</span>
          <h2 className="text-3xl md:text-5xl font-extrabold">Cero Sanciones en más de 50 empresas aliadas</h2>
          <p className="text-slate-400 text-base max-w-xl mx-auto font-medium">Transferimos el 100% de tu riesgo administrativo a nuestro equipo experto para que operes con absoluta tranquilidad.</p>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Pilar 1 */}
          <div className="bg-slate-900 border border-slate-800 p-8 rounded-2xl hover:border-blue-600 transition-all">
            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center text-blue-500 mb-6"><CheckCircle2 size={24} /></div>
            <h3 className="text-xl font-bold mb-2">Co-Responsabilidad Firmada</h3>
            <p className="text-slate-400 text-sm leading-relaxed">No emitimos simples reportes para archivar. Firmamos por contrato la corresponsabilidad sobre la ejecución de tu SG-SST; si surge algún requerimiento o auditoría del Ministerio, nosotros respondemos y damos la cara contigo.</p>
          </div>

          {/* Pilar 2 */}
          <div className="bg-slate-900 border border-slate-800 p-8 rounded-2xl hover:border-blue-600 transition-all">
            <div className="w-12 h-12 bg-orange-500/10 rounded-xl flex items-center justify-center text-orange-500 mb-6"><HardHat size={24} /></div>
            <h3 className="text-xl font-bold mb-2">Matrices de Riesgo Dinámicas</h3>
            <p className="text-slate-400 text-sm leading-relaxed">Diseñamos, actualizamos y evaluamos periódicamente la Matriz de Peligros, Evaluación de Estándares Mínimos y planes de contingencia específicos para el nivel de riesgo de tu actividad económica.</p>
          </div>

          {/* Pilar 3 */}
          <div className="bg-slate-900 border border-slate-800 p-8 rounded-2xl hover:border-blue-600 transition-all">
            <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center text-emerald-400 mb-6"><Activity size={24} /></div>
            <h3 className="text-xl font-bold mb-2">Inspecciones de Campo Seguras</h3>
            <p className="text-slate-400 text-sm leading-relaxed">Ejecutamos auditorías internas, simulacros obligatorios de evacuación e inspecciones periódicas a locaciones, herramientas y equipos de protección (EPP), garantizando evidencias físicas válidas.</p>
          </div>

          {/* Pilar 4 */}
          <div className="bg-slate-900 border border-slate-800 p-8 rounded-2xl hover:border-blue-600 transition-all">
            <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center text-purple-400 mb-6"><Users size={24} /></div>
            <h3 className="text-xl font-bold mb-2">Comités de Ley Conformados</h3>
            <p className="text-slate-400 text-sm leading-relaxed">Estructuramos, capacitamos y hacemos seguimiento continuo a las votaciones, actas y reuniones obligatorias del COPASST, Comité de Convivencia Laboral y Brigadas de Emergencia de tu PyME.</p>
          </div>

          {/* Pilar 5 */}
          <div className="bg-slate-900 border border-slate-800 p-8 rounded-2xl hover:border-blue-600 transition-all">
            <div className="w-12 h-12 bg-indigo-500/10 rounded-xl flex items-center justify-center text-indigo-400 mb-6"><Scale size={24} /></div>
            <h3 className="text-xl font-bold mb-2">Acompañamiento en Auditorías</h3>
            <p className="text-slate-400 text-sm leading-relaxed">¿Recibiste una visita sorpresa o un requerimiento de la ARL o el Ministerio de Trabajo? Nuestro equipo legal y de ingeniería senior asume la defensa técnica presencial de tu empresa inmediatamente.</p>
          </div>

          {/* Pilar 6 */}
          <div className="bg-slate-900 border border-slate-800 p-8 rounded-2xl hover:border-blue-600 transition-all">
            <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center text-teal-400 mb-6"><FileText size={24} /></div>
            <h3 className="text-xl font-bold mb-2">Expedientes en Un Solo Clic</h3>
            <p className="text-slate-400 text-sm leading-relaxed">Centralizamos planillas de asistencia, soportes de capacitación e inducciones de forma 100% digital. Si te solicitan un soporte legal histórico, lo descargas en orden en menos de 5 minutos.</p>
          </div>
        </div>
      </section>

      {/* Carrusel de Google Reviews */}
      <section className="py-20 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="text-center mb-12">
            <span className="text-xs font-bold text-blue-900 uppercase tracking-widest block mb-1">Prueba Social Real</span>
            <h3 className="text-3xl font-extrabold text-slate-900">Opiniones Verificadas en Google Reviews</h3>
          </div>
          
          <div className="relative group/carousel max-w-4xl mx-auto">
            <button onClick={() => handleScrollReviews('left')} className="absolute -left-4 md:-left-12 top-1/2 -translate-y-1/2 z-20 bg-white hover:bg-slate-50 text-gray-700 p-3 rounded-full shadow-md border"><ChevronLeft size={20} /></button>
            <div ref={reviewsContainerRef} className="flex overflow-x-auto snap-x snap-mandatory gap-6 pb-4 scrollbar-hide scroll-smooth" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {GOOGLE_REVIEWS.map((review) => (
                <div key={review.id} className="snap-center shrink-0 w-[84vw] sm:w-[360px] bg-slate-50 p-6 rounded-2xl border flex flex-col relative">
                  <div className="absolute top-4 right-6 text-slate-200"><Quote size={28} fill="currentColor" /></div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-900 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm">{review.initial}</div>
                    <div>
                      <h5 className="font-bold text-gray-900 text-xs md:text-sm line-clamp-1">{review.author_name}</h5>
                      <div className="flex text-yellow-400 gap-0.5 mt-0.5">
                        {[...Array(review.rating)].map((_, i) => <Star key={i} size={10} fill="currentColor" />)}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 text-xs leading-relaxed flex-grow italic">"{review.text}"</p>
                </div>
              ))}
            </div>
            <button onClick={() => handleScrollReviews('right')} className="absolute -right-4 md:-right-12 top-1/2 -translate-y-1/2 z-20 bg-white hover:bg-slate-50 text-gray-700 p-3 rounded-full shadow-md border"><ChevronRightIcon size={20} /></button>
          </div>
        </div>
      </section>

      {/* Footer Minimalista de Conversión */}
      <footer className="landing-footer bg-slate-900 text-slate-400 text-center py-8 text-xs border-t border-slate-800">
        <p>© 2026 Zaphy S.A.S. Todos los derechos reservados. Contacto: <span className="text-emerald-400 font-bold">comercial@zaphy.com.co</span></p>
      </footer>

    </div>
  );
};

export default LandingSST;